import React, {Fragment, useContext, useRef, useState} from "react";
import {Tab} from "@headlessui/react";
import {classes} from "./Leafs";
import PagedSearchTable, {PagedTableFunctions, TableColumn} from "../components/PagedSearchTable";
import AppContext from "../appContext";
import TestTypeController from "../controllers/TestTypeController";
import TestType from "../controllers/TestType";
import TestTypePlacement from "../controllers/TestTypePlacement";
import PlacementUpsert from "../controllers/PlacementUpsert";
import LabCodeUpsert from "../controllers/LabCodeUpsert";
import SubmissionType from "../controllers/SubmissionType";
import LabProviderEnum from "../controllers/LabProviderEnum";
import {useValidation} from "../validation";
import {showSuccessOrFailed} from "../Snacks";
import Dialog from "../components/Dialog";
import WarningPopup, {useWarningState} from "../components/WarningPopup";
import Input from "../components/Input";
import {SelectString} from "../components/SelectString";
import CheckBox from "../components/CheckBox";
import TagInput from "../components/TagInput";
import {classNames, wrapLoader} from "../wrapper";

const dragHandleIcon = <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5"
                            stroke="gray" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/>
</svg>

const SUBMISSION_TYPES: { type: SubmissionType, label: string }[] = [
    {type: SubmissionType.TestLeaf, label: 'Leaf'},
    {type: SubmissionType.TestFarmerSample, label: 'Farmer Sample'},
    {type: SubmissionType.TestSoil, label: 'Soil'},
]

const PROVIDERS: LabProviderEnum[] = [LabProviderEnum.Nvirotek, LabProviderEnum.Wynland, LabProviderEnum.Waypoint]

const submissionTypeOptions: Record<string, string> = {
    'Leaf': SubmissionType.TestLeaf,
    'Farmer Sample': SubmissionType.TestFarmerSample,
    'Soil': SubmissionType.TestSoil,
}

// per-submission-type membership + one shared display name
interface PlacementForm {
    main: boolean;
    optional: boolean;
    name: string;
}

interface FormState {
    id: number;
    code: number;
    enabled: boolean;
    placements: Record<string, PlacementForm>;   // key: SubmissionType
    labCodes: Record<string, string[]>;          // key: LabProviderEnum -> ordered codes
}

function emptyForm(): FormState {
    const placements: Record<string, PlacementForm> = {}
    SUBMISSION_TYPES.forEach(s => placements[s.type] = {main: false, optional: false, name: ''})
    const labCodes: Record<string, string[]> = {}
    PROVIDERS.forEach(p => labCodes[p] = [])
    return {id: 0, code: 0, enabled: true, placements, labCodes}
}

function fromDetail(t: TestType): FormState {
    const form = emptyForm()
    form.id = t.id
    form.code = t.code
    form.enabled = t.enabled
    t.placements.forEach(p => {
        const row = form.placements[p.submissionType] ?? {main: false, optional: false, name: ''}
        if (p.isOptional) row.optional = true; else row.main = true;
        row.name = p.name
        form.placements[p.submissionType] = row
    })
    t.labTestCodes.forEach(c => form.labCodes[c.labProvider] = c.codes)
    return form
}

function toUpsert(form: FormState) {
    const placements: PlacementUpsert[] = []
    SUBMISSION_TYPES.forEach(s => {
        const row = form.placements[s.type]
        if (!row) return
        if (row.main) placements.push({submissionType: s.type, isOptional: false, name: row.name})
        if (row.optional) placements.push({submissionType: s.type, isOptional: true, name: row.name})
    })
    const labCodes: LabCodeUpsert[] = []
    PROVIDERS.forEach(provider => {
        const codes = (form.labCodes[provider] ?? []).map(s => s.trim()).filter(s => s.length > 0)
        if (codes.length > 0) labCodes.push({labProvider: provider, codes})
    })
    return {id: form.id, code: form.code, enabled: form.enabled, placements, labCodes}
}

function placementSummary(t: TestType): string {
    return SUBMISSION_TYPES
        .filter(s => t.placements.some(p => p.submissionType === s.type))
        .map(s => {
            const slots: string[] = []
            if (t.placements.some(p => p.submissionType === s.type && !p.isOptional)) slots.push('main')
            if (t.placements.some(p => p.submissionType === s.type && p.isOptional)) slots.push('optional')
            return `${s.label} (${slots.join(', ')})`
        })
        .join('; ')
}

function distinctNames(t: TestType): string {
    return Array.from(new Set(t.placements.map(p => p.name))).join(' / ')
}

function isPlaced(row: PlacementForm | undefined): boolean {
    return !!row && (row.main || row.optional)
}

function anyPlacement(form: FormState): boolean {
    return SUBMISSION_TYPES.some(s => isPlaced(form.placements[s.type]))
}

function hasAnyLabCode(form: FormState): boolean {
    return PROVIDERS.some(p => (form.labCodes[p] ?? []).some(c => c.trim().length > 0))
}

const ManageTestTypes: React.FC = () => {
    const activeRef = useRef<PagedTableFunctions<TestType>>()
    const archivedRef = useRef<PagedTableFunctions<TestType>>()
    const context = useContext(AppContext)

    const [modalOpen, setModalOpen] = useState<boolean>(false)
    const [form, setForm] = useState<FormState>(emptyForm())

    const archiveWarning = useWarningState<TestType | null>(null)
    const restoreWarning = useWarningState<TestType | null>(null)

    // reorder editor
    const [reorderOpen, setReorderOpen] = useState<boolean>(false)
    const [scope, setScope] = useState<{ submissionType: SubmissionType, isOptional: boolean }>(
        {submissionType: SubmissionType.TestLeaf, isOptional: false})
    const [reorderList, setReorderList] = useState<TestTypePlacement[]>([])
    const [draggedItem, setDraggedItem] = useState<TestTypePlacement | null>(null)
    const [draggedOverIndex, setDraggedOverIndex] = useState<number | null>(null)

    // A placed submission type (main and/or optional) must have a name; and any placed test
    // must have at least one lab code somewhere.
    const validationRules: Record<string, () => boolean> = {
        placements: () => anyPlacement(form),
    }
    SUBMISSION_TYPES.forEach(s => {
        validationRules['name_' + s.type] = () => {
            const row = form.placements[s.type]
            return !isPlaced(row) || (row?.name.trim().length ?? 0) > 0
        }
    })
    const validation = useValidation(validationRules)

    function nameInvalid(type: SubmissionType): boolean {
        return !validation.rules['name_' + type]
    }

    function updatePlacement(type: SubmissionType, patch: Partial<PlacementForm>) {
        setForm(f => {
            const current = f.placements[type] ?? {main: false, optional: false, name: ''}
            return {...f, placements: {...f.placements, [type]: {...current, ...patch}}}
        })
    }

    function updateLabCode(provider: LabProviderEnum, codes: string[]) {
        setForm(f => ({...f, labCodes: {...f.labCodes, [provider]: codes}}))
    }

    function openAdd() {
        setForm(emptyForm())
        setModalOpen(true)
    }

    function openEdit(row: TestType) {
        wrapLoader(context, TestTypeController.detail({id: row.id}), resp => {
            if (resp) setForm(fromDetail(resp))
            setModalOpen(true)
        })
    }

    function save() {
        if (!validation.validate()) return;
        showSuccessOrFailed(context, TestTypeController.upsert(toUpsert(form)))
            .then(() => {
                setModalOpen(false)
                activeRef.current?.refresh()
                archivedRef.current?.refresh()
            })
    }

    function archiveTest(row: TestType) {
        showSuccessOrFailed(context, TestTypeController.archive({id: row.id}))
            .then(() => activeRef.current?.refresh())
    }

    function restoreTest(row: TestType) {
        showSuccessOrFailed(context, TestTypeController.restore({id: row.id}))
            .then(() => archivedRef.current?.refresh())
    }

    function listColumns(archived: boolean): TableColumn<TestType>[] {
        return [
            {header: 'ID', key: 'code', row: row => row.code},
            {header: 'Name', key: 'name', row: row => distinctNames(row)},
            {header: 'Appears in', key: 'placements', row: row => placementSummary(row)},
            {
                header: <div className='text-right'>Actions</div>,
                key: 'actions',
                row: row => <div className='text-right'>
                    <div className="bg-primary btn-sm" onClick={() => openEdit(row)}>edit</div>
                    {archived
                        ? <div className="bg-primary-500 btn-sm"
                               onClick={() => restoreWarning.show(
                                   `Re-activate "${distinctNames(row)}"? It will appear in the submission dropdowns again for new samples.`, row)}>activate</div>
                        : <div className="bg-red-500 btn-sm"
                               onClick={() => archiveWarning.show(
                                   `Archive "${distinctNames(row)}"? It will be hidden from the submission ` +
                                   `dropdowns. Existing samples are unaffected and you can re-activate it anytime.`, row)}>archive</div>}
                </div>
            }
        ]
    }

    // --- reorder ---

    function loadGroup(next: { submissionType: SubmissionType, isOptional: boolean }) {
        setScope(next)
        wrapLoader(context, TestTypeController.group(next), resp => setReorderList(resp))
    }

    function openReorder() {
        setReorderOpen(true)
        loadGroup(scope)
    }

    function onDragOver(index: number) {
        if (index === draggedOverIndex) return;
        setDraggedOverIndex(index)
    }

    function onDragEnd() {
        if (draggedOverIndex !== null && draggedItem !== null) {
            const next = [...reorderList]
            next.splice(reorderList.indexOf(draggedItem), 1)
            next.splice(draggedOverIndex, 0, draggedItem)
            setReorderList(next)
        }
        setDraggedItem(null)
        setDraggedOverIndex(null)
    }

    function saveOrder() {
        showSuccessOrFailed(context, TestTypeController.reorder({ids: reorderList.map(p => p.id)}))
            .then(() => setReorderOpen(false))
    }

    return <div>
        <div className="pb-2 flex items-center">
            <div className="btn bg-primary" onClick={openAdd}>Add Test</div>
            <div className="btn bg-primary-500" onClick={openReorder}>Reorder</div>
        </div>

        <Tab.Group>
            <Tab.List className="flex space-x-1 rounded-xl bg-primary-500 p-1 mb-3">
                <Tab as={Fragment}>{({selected}) => <button className={classes(selected)}>Active</button>}</Tab>
                <Tab as={Fragment}>{({selected}) => <button className={classes(selected)}>Archived</button>}</Tab>
            </Tab.List>
            <Tab.Panels>
                <Tab.Panel>
                    <PagedSearchTable componentRef={activeRef}
                                      call={TestTypeController.pagedActive}
                                      columns={listColumns(false)}/>
                </Tab.Panel>
                <Tab.Panel>
                    <PagedSearchTable componentRef={archivedRef}
                                      call={TestTypeController.pagedArchived}
                                      columns={listColumns(true)}/>
                </Tab.Panel>
            </Tab.Panels>
        </Tab.Group>

        <Dialog title={"Test"} show={modalOpen} setShow={setModalOpen}
                body={<div>
                    <div className="px-4 pt-2">
                        <div className="text-gray-800 font-medium pt-2 pb-1">Appears in</div>
                        {!validation.rules.placements
                            ? <div className="text-xs text-error-600">Select at least one submission type (main or optional).</div>
                            : null}
                        <table className="w-full">
                            <thead>
                            <tr className="text-xs text-gray-500 text-left">
                                <th className="pr-3 py-1 font-medium">Submission type</th>
                                <th className="pr-3 py-1 font-medium">Main</th>
                                <th className="pr-3 py-1 font-medium">Optional</th>
                                <th className="pr-3 py-1 font-medium">Name shown in dropdown</th>
                            </tr>
                            </thead>
                            <tbody>
                            {SUBMISSION_TYPES.map(s => {
                                const row = form.placements[s.type] ?? {main: false, optional: false, name: ''}
                                return <tr key={s.type}>
                                    <td className="pr-3 py-1 whitespace-nowrap">{s.label}</td>
                                    <td className="pr-3 py-1">
                                        <CheckBox checked={row.main}
                                                  onChange={v => updatePlacement(s.type, {main: v})}/>
                                    </td>
                                    <td className="pr-3 py-1">
                                        <CheckBox checked={row.optional}
                                                  onChange={v => updatePlacement(s.type, {optional: v})}/>
                                    </td>
                                    <td className="py-1 w-full">
                                        <Input value={row.name}
                                               change={v => updatePlacement(s.type, {name: v})}
                                               className={nameInvalid(s.type) ? 'border border-red-500' : ''}/>
                                        {nameInvalid(s.type)
                                            ? <div className="text-xs text-error-600">Name required when main or optional is selected</div>
                                            : null}
                                    </td>
                                </tr>
                            })}
                            </tbody>
                        </table>

                        <div className="text-gray-800 font-medium pt-4 pb-1">Lab codes</div>
                        <div className="text-xs text-gray-600 pb-1">Nvirotek &amp; Wynland can take multiple codes (type,
                            then Enter; order matters). Waypoint takes a single code. Leave blank if not sent to that lab.
                        </div>
                        {anyPlacement(form) && !hasAnyLabCode(form)
                            ? <div className="text-xs text-yellow-600">No lab codes — this test can only be submitted manually (it won't be sent to Nvirotek / Wynland / Waypoint).</div>
                            : null}
                        <table className="w-full">
                            <tbody>
                            {PROVIDERS.map(provider => <tr key={provider}>
                                <td className="pr-3 py-2 whitespace-nowrap align-top">{provider}</td>
                                <td className="py-1 w-full">
                                    {provider === LabProviderEnum.Waypoint
                                        ? <Input value={(form.labCodes[provider] ?? [])[0] ?? ''}
                                                 change={v => {
                                                     const digits = v.replace(/\D/g, '')
                                                     updateLabCode(provider, digits.length ? [digits] : [])
                                                 }}/>
                                        : <TagInput value={form.labCodes[provider] ?? []} numeric
                                                    onChange={codes => updateLabCode(provider, codes)}/>}
                                </td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>

                    <div className="text-right p-2 border-t sticky bottom-0 bg-white mt-2">
                        <div className="btn bg-red-500" onClick={() => setModalOpen(false)}>discard</div>
                        <div className="btn bg-primary-500" onClick={() => save()}>
                            {form.id === 0 ? 'insert' : 'update'}
                        </div>
                    </div>
                </div>}/>

        <WarningPopup state={archiveWarning} onYes={row => row && archiveTest(row)}/>

        <WarningPopup state={restoreWarning} onYes={row => row && restoreTest(row)}/>

        <Dialog title={"Reorder tests"} show={reorderOpen} setShow={setReorderOpen}
                body={<div className="px-4 pt-4 min-w-[500px]">
                    <div className="flex items-center pb-3">
                        <div className="pr-2 text-gray-800 whitespace-nowrap">Submission type</div>
                        <SelectString value={scope.submissionType}
                                      onChange={v => loadGroup({...scope, submissionType: v as SubmissionType})}
                                      options={submissionTypeOptions}/>
                    </div>
                    <Tab.Group selectedIndex={scope.isOptional ? 1 : 0}
                               onChange={index => loadGroup({...scope, isOptional: index === 1})}>
                        <Tab.List className="flex space-x-1 rounded-xl bg-primary-500 p-1 mb-3">
                            <Tab as={Fragment}>
                                {({selected}) => <button className={classes(selected)}>Standard Tests</button>}
                            </Tab>
                            <Tab as={Fragment}>
                                {({selected}) => <button className={classes(selected)}>Optional Tests</button>}
                            </Tab>
                        </Tab.List>
                    </Tab.Group>
                    <div className="text-xs text-gray-600 pb-2">Drag a row to change its position in the dropdown.</div>
                    <table className="w-full">
                        <tbody>
                        {reorderList.map((row, index) => <tr
                            key={row.id}
                            draggable={true}
                            onDragStart={() => setDraggedItem(row)}
                            onDragOver={e => {
                                e.preventDefault()
                                onDragOver(index)
                            }}
                            onDragEnd={onDragEnd}
                            className={classNames(index % 2 === 0 ? "" : "bg-gray-50", "hover:bg-gray-200 cursor-move")}
                        >
                            <td className="pr-3 py-1 w-6">{dragHandleIcon}</td>
                            <td className="pr-3 py-1 text-gray-500 w-8">{index + 1}</td>
                            <td className="pr-3 py-1">{row.name}</td>
                        </tr>)}
                        {reorderList.length === 0
                            ? <tr>
                                <td className="py-2 text-gray-500">No tests in this group.</td>
                            </tr>
                            : null}
                        </tbody>
                    </table>
                    <div className="text-right py-2 border-t sticky bottom-0 bg-white mt-2">
                        <div className="btn bg-red-500" onClick={() => setReorderOpen(false)}>discard</div>
                        <div className="btn bg-primary-500" onClick={() => saveOrder()}>save order</div>
                    </div>
                </div>}/>
    </div>
}

export default ManageTestTypes;
